import { Column } from 'primereact/column';
import { DataTable, type DataTableExpandedRows, type DataTableValue } from 'primereact/datatable';
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { MdArrowDropDown, MdArrowRight } from 'react-icons/md';
import {
	buildPaginatorTemplate,
	PAGINATOR_CURRENT_REPORT,
	paginatorPassThrough,
} from '../../../../components/table/pagination/Paginator';
import RowExpansionTemplate from '../../../../components/table/table-content/TransactionRowExpansion';
import styles from './home-table.module.css';
import { X4bActivityDto } from '../../../vehicle/engine/X4bActivityDto';
import { formatCurrency } from '@/common/common_utils';
import { DateHelper } from '@/common/DateHelper';
import EmptyTable from '@/components/empty/EmptyTable';

interface HomeTableProps {
	//columns: { field: keyof T; header: string; sortable?: boolean }[];
	rowsPerPage?: number;
	emptyState?: 'noData' | 'noSearchData' | 'noFilterData';
	loading?: boolean;
	onRowsChange: (rows: number) => void;
	expandedActivityId?: number | null;
	onExpandedActivityChange?: (id: number | null) => void;
	value?: DataTableValue[];
	[x: string]: any; // <-- This allows pass through arbitrary DataTable props
}

const renderEmptyState = (state?: 'noData' | 'noSearchData' | 'noFilterData') => {
	switch (state) {
		case 'noData':
			return EmptyTable({
				title: 'There are no active rides right now',
				src: '/images/no_active_rides.png',
			});
		case 'noSearchData':
			return EmptyTable({
				title: 'We didn’t find a match',
				description: 'Try searching for something else.',
				src: '/images/svg/no-result.svg',
				descriptionClassName: 'text-primaryContainer',
			})
		case 'noFilterData':
			return EmptyTable({
				title: 'Sorry we couldn’t find any results',
				src: '/images/svg/no-filter.svg',
			});
		// case 'error':
		// 	return (
		// 		<EmptyState title="Something went wrong" variant="triangle" />
		// 	);
		default:
			return null;
	}
};

const getStatusStyle = (status: string) => {
	switch (status) {
		case 'On Trip':
			return styles.statusOnTrip;
		case 'Completed':
			return styles.statusCompleted;
		case 'Cancelled':
			return styles.statusCancelled;
		default:
			return styles.statusDefault;
	}
};

const HomeTable = forwardRef<any, HomeTableProps>(({
	columns,
	rowsPerPage = 5,
	emptyState,
	loading,
	onRowsChange,
	expandedActivityId,
	onExpandedActivityChange,
	value,
	...rest // <-- Collect all other props
}, ref) => {
	const [expandedRows, setExpandedRows] = useState<
		DataTableExpandedRows | DataTableValue[] | undefined
	>(undefined);

	// Expand the row when expandedActivityId changes
	useEffect(() => {
		if (expandedActivityId && value) {
			const activity = (value as X4bActivityDto[]).find((item) => item.Id === expandedActivityId);
			if (activity) {
				setExpandedRows([activity as DataTableValue]);
			}
		}
	}, [expandedActivityId, value]);

	const isRowExpanded = (rowData: DataTableValue) =>
		Array.isArray(expandedRows)
			? expandedRows.some((row) => row === rowData)
			: false;

	const handleRowToggle = (rowData: DataTableValue) => {
		const newExpandedRows = isRowExpanded(rowData) ? [] : [rowData];
		setExpandedRows(newExpandedRows);

		// Notify parent of expanded row change
		if (onExpandedActivityChange) {
			const activity = rowData as X4bActivityDto;
			onExpandedActivityChange(isRowExpanded(rowData) ? null : activity.Id);
		}
	};

	useImperativeHandle(ref, () => ({
		collapseAll: () => setExpandedRows([]),
	}));

	return (
		<DataTable
			className={styles.customDatatable}
			tableStyle={{ minWidth: '50rem', width: '100%' }}
			pt={{ paginator: paginatorPassThrough }}
			paginatorTemplate={buildPaginatorTemplate(onRowsChange)}
			currentPageReportTemplate={PAGINATOR_CURRENT_REPORT}
			emptyMessage={loading ? null : renderEmptyState(emptyState)}
			expandedRows={expandedRows}
			onRowToggle={(e) => {
				handleRowToggle(e.data);
			}}
			rowExpansionTemplate={(rowData: X4bActivityDto) => <RowExpansionTemplate data={rowData} />}
			value={value}
			{...rest} // <-- Spread the rest of the props here
		>
			<Column
				header=""
				body={(rowData) =>
					loading ? null : (
						<button
							type="button"
							onClick={() => {
								handleRowToggle(rowData);
							}}
							className={styles.expandToggle}
						>
							{isRowExpanded(rowData) ? (
								<MdArrowDropDown
									size={24}
									className="text-tertiary"
								/>
							) : (
								<MdArrowRight
									size={24}
									className="text-black"
								/>
							)}
						</button>
					)
				}
				style={{ width: '3rem', textAlign: 'center' }}
			/>
			<Column field="Date" header="Date" sortable
				body={(activity: X4bActivityDto) => activity.Date ? DateHelper.formatDate(activity.Date) : ''}
			/>
			<Column field="CreatorNameFull" header="Name" sortable />
			<Column field="Location" header="Location" sortable
				body={(activity: X4bActivityDto) => activity.Pickup?.Name ?? activity.Pickup?.PlaceName ?? ''}
			/>
			<Column field="ProgramName" header="Program" sortable />
			<Column field="Amount" header="Amount" sortable
				body={(activity: X4bActivityDto) => {
					return <span											>
						{formatCurrency(activity.Amount)}
					</span>
				}}
			/>
			<Column field="Status" header="Status" sortable
				body={(activity: X4bActivityDto) => {
					return <span
						className={`${styles.statusBadge} ${getStatusStyle(activity.StatusName ?? "")}`}					>
						{activity.StatusName}
					</span>
				}}
			/>
		</DataTable>
	);
});

HomeTable.displayName = 'HomeTable';

export default HomeTable;
